import type React from "react";
import { Redirect, Route, Router, Switch } from "wouter";
import { Layout } from "./components/Layout/Layout";
import { useAuth } from "./context/AuthContext";
import Analytics from "./pages/Analytics";
import AnalyticsTokens from "./pages/AnalyticsTokens";
import { ApiKeyDetail } from "./pages/ApiKeyDetail";
import { ApiKeys } from "./pages/ApiKeys";
import { AuditLogs } from "./pages/AuditLogs";
import AdminApiKeys from "./pages/admin/AdminApiKeys";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminSessions from "./pages/admin/AdminSessions";
import AdminUsage from "./pages/admin/AdminUsage";
import AdminUsers from "./pages/admin/AdminUsers";
import { AdminLogin } from "./pages/auth/AdminLogin";
import { LandingAuth } from "./pages/auth/LandingAuth";
import { Login } from "./pages/auth/Login";
import { Signup } from "./pages/auth/Signup";
import { Dashboard } from "./pages/Dashboard";
import { Models } from "./pages/Models";
import { EditModel } from "./pages/models/EditModel";
import { EditModelConfig } from "./pages/models/EditModelConfig";
import { NewModel } from "./pages/models/NewModel";
import { NewModelConfig } from "./pages/models/NewModelConfig";
import { NotFound } from "./pages/NotFound";
import Playground from "./pages/Playground";
import { Projects } from "./pages/Projects";
import { Providers } from "./pages/Providers";
import RequestLogs from "./pages/RequestLogs";
import { RagCollection } from "./pages/rag/RagCollection";
import { SuperAdminDashboard } from "./pages/SuperAdminDashboard";
import { UserModels } from "./pages/UserModels";
import { Users } from "./pages/Users";

// Auth page wrapper for standalone login/signup pages - redirects if already logged in
function AuthPageWrapper({
	children,
	title,
}: {
	children: React.ReactNode;
	title: string;
}) {
	const { user, isLoading } = useAuth();

	// Redirect to dashboard if already logged in and authorized
	if (!isLoading && user) {
		const isAuthorized =
			user.status === "active" &&
			(user.role === "superAdmin" ||
				user.role === "admin" ||
				!user.approvalStatus ||
				user.approvalStatus === "approved");

		if (isAuthorized) {
			return <Redirect to="/dashboard" />;
		}
	}

	return (
		<div className="min-h-screen bg-background flex items-center justify-center p-4">
			<div className="w-full max-w-md">
				<div className="flex items-center justify-center mb-8">
					<img
						src="/assets/novaroute-wordmark-light.svg"
						alt="Nova-route"
						className="h-7"
					/>
				</div>
				<div className="rounded-3xl border border-border/50 bg-card p-8 shadow-xl">
					<h2 className="text-2xl font-bold tracking-tight text-foreground mb-6">
						{title}
					</h2>
					{children}
				</div>
			</div>
		</div>
	);
}

// Protected route wrapper that checks authentication
function ProtectedRoute({
	component: Component,
}: {
	component: React.ComponentType;
}) {
	const { isAuthenticated, user, isLoading } = useAuth();

	// Show loading while auth state is being determined
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
					<p className="mt-2 text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	// Allow access if authenticated and user is active
	// Users with no approvalStatus are allowed (regular users)
	// Users with approvalStatus must be 'approved'
	const isAuthorized =
		isAuthenticated &&
		user?.status === "active" &&
		(user?.role === "superAdmin" ||
			user?.role === "admin" ||
			!user?.approvalStatus ||
			user?.approvalStatus === "approved");

	if (!isAuthorized) {
		return <Redirect to="/" />;
	}

	return (
		<Layout>
			<Component />
		</Layout>
	);
}

// Super Admin only route wrapper
function SuperAdminRoute({
	component: Component,
}: {
	component: React.ComponentType;
}) {
	const { isAuthenticated, user, isLoading } = useAuth();

	// Show loading while auth state is being determined
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
					<p className="mt-2 text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	// Debug logging
	console.log("[SuperAdminRoute] Auth check:", {
		isAuthenticated,
		userRole: user?.role,
		userStatus: user?.status,
		userEmail: user?.email,
	});

	// Only allow superAdmin
	const isAuthorized =
		isAuthenticated && user?.status === "active" && user?.role === "superAdmin";

	if (!isAuthorized) {
		console.log("[SuperAdminRoute] Not authorized, redirecting to /dashboard");
		return <Redirect to="/dashboard" />;
	}

	return (
		<Layout>
			<Component />
		</Layout>
	);
}

function App() {
	return (
		<Router>
			<Switch>
				{/* Redirect /auth/* routes to frontend routes */}
				<Route path="/auth/signup">
					<Redirect to="/signup" />
				</Route>
				<Route path="/auth/login">
					<Redirect to="/login" />
				</Route>

				{/* User Auth Routes */}
				<Route path="/signup">
					<AuthPageWrapper title="Create Account">
						<Signup />
					</AuthPageWrapper>
				</Route>
				<Route path="/login">
					<AuthPageWrapper title="Sign In">
						<Login />
					</AuthPageWrapper>
				</Route>

				{/* Admin Login Route - Separate from regular login */}
				<Route path="/n-r-ops">
					<div className="min-h-screen bg-background flex items-center justify-center p-4">
						<div className="w-full max-w-md">
							<div className="flex items-center justify-center mb-8">
								<img
									src="/assets/novaroute-wordmark-light.svg"
									alt="Nova-route"
									className="h-7"
								/>
							</div>
							<div className="rounded-3xl border border-border/50 bg-card p-8 shadow-xl">
								<h2 className="text-2xl font-bold tracking-tight text-foreground mb-6">
									Administrator Sign In
								</h2>
								<AdminLogin />
							</div>
						</div>
					</div>
				</Route>

				{/* Super Admin Routes - n-r-ops prefix */}
				<Route path="/n-r-ops/dashboard">
					<SuperAdminRoute component={SuperAdminDashboard} />
				</Route>
				<Route path="/n-r-ops/users">
					<SuperAdminRoute component={AdminUsers} />
				</Route>
				<Route path="/n-r-ops/api-keys">
					<SuperAdminRoute component={AdminApiKeys} />
				</Route>
				<Route path="/n-r-ops/projects">
					<SuperAdminRoute component={AdminProjects} />
				</Route>
				<Route path="/n-r-ops/sessions">
					<SuperAdminRoute component={AdminSessions} />
				</Route>
				<Route path="/n-r-ops/usage">
					<SuperAdminRoute component={AdminUsage} />
				</Route>
				<Route path="/n-r-ops/audit">
					<SuperAdminRoute component={AdminAuditLogs} />
				</Route>
				{/* Redirect /admin/* to /n-r-ops/* for backward compatibility */}
				<Route path="/admin">
					<Redirect to="/n-r-ops/dashboard" />
				</Route>
				<Route path="/admin/users">
					<Redirect to="/n-r-ops/users" />
				</Route>
				<Route path="/admin/api-keys">
					<Redirect to="/n-r-ops/api-keys" />
				</Route>
				<Route path="/admin/projects">
					<Redirect to="/n-r-ops/projects" />
				</Route>
				<Route path="/admin/sessions">
					<Redirect to="/n-r-ops/sessions" />
				</Route>
				<Route path="/admin/usage">
					<Redirect to="/n-r-ops/usage" />
				</Route>
				<Route path="/admin/audit">
					<Redirect to="/n-r-ops/audit" />
				</Route>

				{/* User Dashboard and Admin Pages (protected) */}
				<Route path="/dashboard">
					<ProtectedRoute component={Dashboard} />
				</Route>
				<Route path="/projects">
					<ProtectedRoute component={Projects} />
				</Route>
				<Route path="/api-keys/:keyId">
					<ProtectedRoute component={ApiKeyDetail} />
				</Route>
				<Route path="/api-keys">
					<ProtectedRoute component={ApiKeys} />
				</Route>
				<Route path="/users">
					<SuperAdminRoute component={Users} />
				</Route>
				<Route path="/providers">
					<ProtectedRoute component={Providers} />
				</Route>
				<Route path="/route-configs/:id/rag">
					<ProtectedRoute component={RagCollection} />
				</Route>
				<Route path="/route-configs/:id/edit">
					<ProtectedRoute component={EditModelConfig} />
				</Route>
				<Route path="/route-configs/new">
					<ProtectedRoute component={NewModelConfig} />
				</Route>
				<Route path="/models/manage/:id/edit">
					<ProtectedRoute component={EditModel} />
				</Route>
				<Route path="/models/manage/new">
					<ProtectedRoute component={NewModel} />
				</Route>
				<Route path="/models/manage">
					<ProtectedRoute component={UserModels} />
				</Route>
				<Route path="/route-configs">
					<ProtectedRoute component={Models} />
				</Route>
				<Route path="/analytics">
					<ProtectedRoute component={Analytics} />
				</Route>
				<Route path="/analytics/tokens">
					<ProtectedRoute component={AnalyticsTokens} />
				</Route>
				<Route path="/playground">
					<ProtectedRoute component={Playground} />
				</Route>
				<Route path="/audit">
					<ProtectedRoute component={AuditLogs} />
				</Route>
				<Route path="/logs">
					<ProtectedRoute component={RequestLogs} />
				</Route>

				{/* Default landing */}
				<Route path="/" component={LandingAuth} />

				{/* 404 - Catch all unmatched routes */}
				<Route component={NotFound} />
			</Switch>
		</Router>
	);
}

export default App;
